import { Injectable, inject } from '@angular/core';
import { Observable, map, from, switchMap } from 'rxjs';
import { resolveBusinessErrorMessage } from '../errors/business-error-messages';
import { WithdrawalApi } from '../apis/withdrawal-api';
import { PinEncryptionService } from './pin-encryption.service';
import type {
  TopupMethod,
  TopupToken,
  WithdrawalAuthTxnResult,
  InitWithdrawalResponse,
  InitWithdrawalParams,
} from '../models';

/**
 * WithdrawalService
 *
 * Business-logic layer between WithdrawalStore and WithdrawalApi.
 *
 * Responsibilities:
 *  - Unwrap API envelopes (`{ success, methods }` → `methods[]`)
 *  - Return raw API data; S3 URL resolution is done reactively in the UI layer
 *  - Encrypt the raw PIN via PinEncryptionService before calling verifyPin
 *  - Keep WithdrawalStore free of direct API, crypto, or config dependencies
 *
 * Architecture: Component → Store → Service → Api → Backend
 */
@Injectable({
  providedIn: 'root',
})
export class WithdrawalService {
  private readonly api = inject(WithdrawalApi);
  private readonly pinEncryption = inject(PinEncryptionService);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch withdrawal methods available for a given amount. Icon paths are
   * returned as raw relative S3 keys; the S3 base URL is applied reactively in
   * the UI layer so icons resolve once runtime config loads.
   */
  getMethods(amount: number): Observable<TopupMethod[]> {
    return this.api.getMethods(amount).pipe(map((res) => res.methods));
  }

  // ── 2. Tokens ──────────────────────────────────────────────────────────────

  /**
   * Fetch saved withdrawal tokens (payment accounts) for the given processor.
   * Only tokens with token_service = 2 (WITHDRAWAL) are returned by the API.
   */
  getTokens(processorId: string): Observable<TopupToken[]> {
    return this.api.getTokens(processorId).pipe(
      map((res) => {
        if (!res.success) {
          // HTTP 200 with `success: false` — the code is the only reliable signal.
          // Goes through the registry rather than an inline `=== '451'` so every
          // mapped business code gets its proper message here too.
          throw new Error(
            resolveBusinessErrorMessage(
              res.error_code,
              'Impossible de charger vos comptes enregistrés. Vérifiez votre connexion et réessayez.',
            ),
          );
        }
        return res.tokens ?? [];
      }),
    );
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the withdrawal.
   * Returns the full result including withdrawal_id, fee, categories.
   *
   * @param amount      - Amount to withdraw
   * @param processorId - ID of the selected processor
   * @param tokenId     - ID of the selected token (_id)
   * @param methodId    - ID of the selected TopupMethod (method.id)
   */
  authTxn(
    amount: number,
    processorId: string,
    tokenId: string,
    methodId: string,
  ): Observable<WithdrawalAuthTxnResult> {
    return this.api
      .authTxn(amount, processorId, tokenId, methodId)
      .pipe(map((res) => ({ ...res.authorize_trxn, withdrawal_id: res.withdrawals._id })));
  }

  // ── 4. Verify PIN ──────────────────────────────────────────────────────────

  /**
   * Encrypt the raw 4-digit PIN, then verify it against the backend.
   *
   * Encryption happens here (not in the store) so the store never handles
   * plaintext PINs after the initial keypad emission.
   *
   * The backend returns `{ success: true }` on success — there is no authentication_data
   * in the response. The raw PIN itself is used as authentication_data in initWithdrawal.
   *
   * @param rawPin      - Plain 4-digit string entered by the user
   * @param withdrawalId - Withdrawal record ID from auth_txn
   */
  verifyPin(rawPin: string, withdrawalId: string): Observable<void> {
    return from(this.pinEncryption.encryptPin(rawPin)).pipe(
      switchMap((encryptedPin) => this.api.verifyPin(encryptedPin, withdrawalId)),
      map(() => undefined),
    );
  }

  // ── 5. Init withdrawal ─────────────────────────────────────────────────────

  /**
   * Execute the withdrawal and return the raw backend response.
   * Must be called immediately after verifyPin succeeds.
   */
  initWithdrawal(params: InitWithdrawalParams): Observable<InitWithdrawalResponse> {
    return this.api.initWithdrawal(params);
  }
}
